import React from 'react';

/**
 * Capa de livro do design 2.0: imagem com "lombada" sutil e zoom leve no hover,
 * ou, sem imagem, uma capa gerada em tom pastel (escolhido pelo título) com título editorial.
 * Usada pela estante (/library) e pela Biblioteca Online (/libritui-library).
 */
const TINTS = [
  { bg: 'bg-tint-lavender', ink: 'text-ink' },
  { bg: 'bg-tint-sage', ink: 'text-ink' },
  { bg: 'bg-tint-peach', ink: 'text-ink' },
  { bg: 'bg-tint-sky', ink: 'text-ink' },
  { bg: 'bg-tint-butter', ink: 'text-ink' },
  { bg: 'bg-tint-rose', ink: 'text-ink' },
];

const hash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

interface BookCoverArtProps {
  src?: string;
  title: string;
  author?: string;
  isSmall?: boolean;
  onError?: () => void;
}

export const BookCoverArt: React.FC<BookCoverArtProps> = ({ src, title, author, isSmall = false, onError }) => {
  if (src) {
    return (
      <div className="w-full h-full relative overflow-hidden bg-tint-stone">
        <img
          src={src}
          alt={title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
          onError={onError}
        />
        {/* lombada: faixa de luz e sombra à esquerda, como um livro de verdade */}
        <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-[7%] bg-gradient-to-r from-black/25 via-white/10 to-transparent" />
        <span aria-hidden className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.06]" style={{ borderRadius: 'inherit' }} />
      </div>
    );
  }

  const tint = TINTS[hash(title || '') % TINTS.length];
  return (
    <div className={`w-full h-full relative overflow-hidden ${tint.bg} ${tint.ink} flex flex-col`}>
      <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-[7%] bg-gradient-to-r from-black/15 via-white/10 to-transparent" />
      <span aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/30 blur-2xl transition-transform duration-700 group-hover:scale-125" />
      <div className={`relative flex-1 flex flex-col ${isSmall ? 'p-2.5 pl-4' : 'p-5 pl-7'}`}>
        {!isSmall && <span className="font-mono text-[9px] uppercase tracking-[0.16em] opacity-50">libritui.</span>}
        <h4 className={`font-display font-medium tracking-[-0.03em] leading-[1.08] ${isSmall ? 'text-[10px] line-clamp-5' : 'text-xl mt-auto line-clamp-5'}`}>{title}</h4>
        {!isSmall && author && (
          <>
            <span className="block w-8 h-px bg-current opacity-30 my-3" />
            <p className="text-[11px] opacity-60 line-clamp-1">{author}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default BookCoverArt;
